// PointsService - Serviço para gerenciar sistema de pontuação e gamificação
import { supabase } from '../lib/supabase';
import { runOnlineOrToast } from '../utils/networkGuard';
import { logger } from '../utils/logger';

// Helper para fazer requisições REST ao Supabase (para pontos)
const pointsRestRequest = async (
  endpoint: string,
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  body?: any
) => {
  let accessToken = '';

  try {
    const { data } = await supabase.auth.getSession();
    accessToken = data?.session?.access_token || '';
  } catch (error) {
    logger.error('Erro ao buscar sessão ativa no Supabase', error);
  }

  if (!accessToken) {
    const tokensStr = localStorage.getItem('supabase.auth.token');
    if (tokensStr) {
      try {
        const tokens = JSON.parse(tokensStr);
        accessToken = tokens.access_token;
      } catch (e) {
        logger.error('Erro ao parsear tokens do localStorage em pointsService', e);
      }
    }
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
    'Prefer': 'return=representation',
    'Cache-Control': 'no-store'
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const options: RequestInit = {
    method,
    headers,
    cache: 'no-store'
  };

  if (body && (method === 'POST' || method === 'PATCH')) {
    options.body = JSON.stringify(body);
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000);

  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/${endpoint}`,
      { ...options, signal: controller.signal }
    );

    if (!response.ok) {
      let errMsg = `Request failed with status ${response.status}`;
      try {
        const err = await response.json();
        errMsg = (err && (err.message || err.error)) || errMsg;
      } catch {
        // ignore parse error
      }
      throw new Error(errMsg);
    }
    return response.json();
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') {
      throw new Error('Tempo limite excedido ao se comunicar com o servidor. Tente novamente.');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export interface PointRule {
  id: string;
  name: string;
  description: string;
  points: number;
  category: 'positive' | 'negative' | 'streak';
  icon: string;
  active: boolean;
  appliesTo: 'member' | 'family';
  createdAt: string;
  updatedAt: string;
}

export interface StreakBonus {
  id: string;
  days: number;
  multiplier: number;
  bonus: number;
  type: 'treatment' | 'illness' | 'allopathic';
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MemberPoints {
  id: string;
  memberId: string;
  totalPoints: number;
  medicationPoints?: number;
  badgesPoints?: number;
  quizPoints?: number;
  currentTreatmentStreak: number;
  currentIllnessStreak: number;
  currentAllopathicStreak: number;
  longestTreatmentStreak: number;
  longestIllnessStreak: number;
  longestAllopathicStreak: number;
  lastMedicationDate?: string;
  lastIllnessDate?: string;
  lastAllopathicDate?: string;
  lastActivityDate?: string;
  level: number;
  createdAt: string;
  updatedAt: string;
}

export interface FamilyPoints {
  id: string;
  familyId: string;
  totalPoints: number;
  totalCuriositiesRead: number;
  totalStreaksAchieved?: number;
  level: number;
  createdAt: string;
  updatedAt: string;
}

export interface PointEvent {
  id: string;
  userId: string;
  memberId?: string;
  familyId?: string;
  eventType: string;
  points: number;
  description?: string;
  badgeName?: string;
  levelTitle?: string;
  medicationDetail?: string;
  medicalDetail?: string;
  source?: 'legacy' | 'v2';
  appliesTo: 'member' | 'family';
  ruleId?: string;
  createdAt: string;
}

export interface StreakAchievement {
  id: string;
  memberId: string;
  streakBonusId: string;
  achievedAt: string;
  pointsAwarded: number;
}

export interface MemberRanking {
  memberId: string;
  name: string;
  totalPoints: number;
  level: number;
  rank: number;
}

export interface FamilyRanking {
  familyId: string;
  familyName: string;
  totalPoints: number;
  level: number;
  rank: number;
}

export interface LevelTitle {
  id: string;
  level: number;
  title: string;
  description?: string;
  appliesTo: 'member' | 'family';
  icon?: string;
  color?: string;
  minPoints: number;
  maxPoints?: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Achievement {
  type: 'level_up' | 'streak_milestone' | 'quiz_master' | 'points_milestone';
  title: string;
  description: string;
  points: number;
  icon: string;
  metadata?: {
    oldLevel?: number;
    newLevel?: number;
    streakType?: string;
    streakDays?: number;
    levelTitle?: string;
    subtitle?: string;
  };
}

export interface BadgeCriteria {
  type: 'treatment_streak' | 'illness_streak' | 'allopathic_streak' | 'quiz_completed' | 'level_reached' | 'doses_on_time' | 'first_dose' | 'first_treatment' | 'days_active' | 'treatment_completed';
  days?: number;
  count?: number;
  level?: number;
}

export interface Badge {
  id: string;
  name: string;
  description: string | null;
  icon: string;
  category: 'streak' | 'quiz' | 'level' | 'special';
  tier: 'bronze' | 'silver' | 'gold' | 'diamond' | 'platinum';
  criteria: BadgeCriteria;
  pointsReward: number;
  color: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MemberBadge {
  id: string;
  memberId: string;
  badgeId: string;
  pointsEarned: number;
  unlockedAt: string;
  progress: number;
  metadata: Record<string, any> | null;
  createdAt: string;
  shownAt: string | null;
  badge?: Badge; // Badge completo quando fazemos join
}

// ==================== HELPER FUNCTIONS ====================

function convertPointRuleFromDB(data: any): PointRule {
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    points: data.points,
    category: data.category,
    icon: data.icon,
    active: data.active,
    appliesTo: data.applies_to,
    createdAt: data.created_at,
    updatedAt: data.updated_at
  };
}

function convertMemberPointsFromDB(data: any): MemberPoints {
  return {
    id: data.id,
    memberId: data.member_id,
    totalPoints: data.total_points,
    medicationPoints: data.medication_points,
    badgesPoints: data.badges_points,
    quizPoints: data.quiz_points,
    currentTreatmentStreak: data.current_treatment_streak,
    currentIllnessStreak: data.current_illness_streak,
    currentAllopathicStreak: data.current_allopathic_streak,
    longestTreatmentStreak: data.longest_treatment_streak,
    longestIllnessStreak: data.longest_illness_streak,
    longestAllopathicStreak: data.longest_allopathic_streak,
    lastMedicationDate: data.last_treatment_date,
    lastIllnessDate: data.last_illness_date,
    lastAllopathicDate: data.last_allopathic_date,
    lastActivityDate: data.last_activity_date,
    level: data.level,
    createdAt: data.created_at,
    updatedAt: data.updated_at
  };
}

function convertLevelTitleFromDB(data: any): LevelTitle {
  return {
    id: data.id,
    level: data.level,
    title: data.title,
    description: data.description,
    appliesTo: data.applies_to,
    icon: data.icon,
    color: data.color,
    minPoints: data.min_points,
    maxPoints: data.max_points,
    active: data.active,
    createdAt: data.created_at,
    updatedAt: data.updated_at
  };
}

// ==================== POINTS SERVICE ====================

class PointsService {
  private static instance: PointsService;

  private constructor() {}

  static getInstance(): PointsService {
    if (!PointsService.instance) {
      PointsService.instance = new PointsService();
    }
    return PointsService.instance;
  }

  // ==================== ADMIN - REGRAS DE PONTUAÇÃO ====================

  /**
   * Busca todas as regras de pontuação
   */
  async getAllRules(): Promise<PointRule[]> {
    const { data, error } = await supabase
      .from('point_rules' as any)
      .select('id, name, description, points, category, icon, active, applies_to, created_at, updated_at')
      .order('category', { ascending: true })
      .order('points', { ascending: false });

    if (error) {
      logger.error('Erro ao buscar regras:', error);
      throw error;
    }

    return (data || []).map(convertPointRuleFromDB);
  }

  /**
   * Busca regras ativas
   */
  async getActiveRules(): Promise<PointRule[]> {
    const { data, error } = await supabase
      .from('point_rules' as any)
      .select('id, name, description, points, category, icon, active, applies_to, created_at, updated_at')
      .eq('active', true)
      .order('category', { ascending: true });

    if (error) {
      logger.error('Erro ao buscar regras ativas:', error);
      throw error;
    }

    return (data || []).map(convertPointRuleFromDB);
  }

  /**
   * Busca regra por ID
   */
  async getRuleById(id: string): Promise<PointRule | null> {
    const { data, error } = await supabase
      .from('point_rules' as any)
      .select('id, name, description, points, category, icon, active, applies_to, created_at, updated_at')
      .eq('id', id)
      .single();

    if (error) {
      logger.error('Erro ao buscar regra:', error);
      return null;
    }

    return data ? convertPointRuleFromDB(data) : null;
  }

  /**
   * Cria nova regra - ADMIN ONLY
   */
  async createRule(rule: Omit<PointRule, 'id' | 'createdAt' | 'updatedAt'>): Promise<PointRule> {
    const { data, error } = await runOnlineOrToast(async () =>
      supabase
        .from('point_rules' as any)
        .insert([{
          name: rule.name,
          description: rule.description,
          points: rule.points,
          category: rule.category,
          icon: rule.icon,
          active: rule.active,
          applies_to: rule.appliesTo
        }])
        .select('id, name, description, points, category, icon, active, applies_to, created_at, updated_at')
        .single()
    );

    if (error) {
      logger.error('Erro ao criar regra:', error);
      throw error;
    }

    return convertPointRuleFromDB(data);
  }

  /**
   * Atualiza regra existente - ADMIN ONLY
   */
  async updateRule(id: string, updates: Partial<Omit<PointRule, 'id' | 'createdAt' | 'updatedAt'>>): Promise<PointRule> {
    const dbUpdates: any = { updated_at: new Date().toISOString() };
    
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    if (updates.points !== undefined) dbUpdates.points = updates.points;
    if (updates.category !== undefined) dbUpdates.category = updates.category;
    if (updates.icon !== undefined) dbUpdates.icon = updates.icon;
    if (updates.active !== undefined) dbUpdates.active = updates.active;
    if (updates.appliesTo !== undefined) dbUpdates.applies_to = updates.appliesTo;

    const { data, error } = await runOnlineOrToast(async () =>
      supabase
        .from('point_rules' as any)
        .update(dbUpdates)
        .eq('id', id)
        .select('id, name, description, points, category, icon, active, applies_to, created_at, updated_at')
        .single()
    );

    if (error) {
      logger.error('Erro ao atualizar regra:', error);
      throw error;
    }

    return convertPointRuleFromDB(data);
  }

  /**
   * Deleta regra - ADMIN ONLY
   */
  async deleteRule(id: string): Promise<void> {
    const { error } = await runOnlineOrToast(async () =>
      supabase
        .from('point_rules' as any)
        .delete()
        .eq('id', id)
    );

    if (error) {
      logger.error('Erro ao deletar regra:', error);
      throw error;
    }
  }

  // ==================== PONTOS DE MEMBROS ====================

  /**
   * Busca pontos de um membro (usando API REST)
   * ✅ MIGRADO: Agora lê de member_points_v2
   */
  async getMemberPoints(memberId: string): Promise<MemberPoints | null> {
    try {
      // ✅ FASE 2: Migração para v2
      const data = await pointsRestRequest(
        `member_points_v2?member_id=eq.${memberId}`,
        'GET'
      );

      if (!data || data.length === 0) {
        logger.error('Pontos do membro não encontrados');
        return null;
      }

      const v2Data = data[0];
      
      return convertMemberPointsFromDB({
        ...v2Data,
        total_points: v2Data.total_points ?? 0,
        medication_points: v2Data.medication_points ?? 0,
        badges_points: v2Data.badges_points ?? 0,
        quiz_points: v2Data.quiz_points ?? 0,
        level: v2Data.level ?? 1
      });
    } catch (error) {
      logger.error('❌ [PointsService] Erro ao buscar pontos do membro:', error);
      return null;
    }
  }



  

  /**
   * Aplica regra de pontuação para um membro
   */
  async applyPointRule(
    memberId: string, 
    _ruleId: string, 
    _userId: string,
    _metadata?: any
  ): Promise<number> {
    logger.warn('⚠️ [PointsService] applyPointRule() desativada (v1). Use fluxos v2 baseados em triggers.');
    const current = await this.getMemberPoints(memberId);
    return current?.totalPoints ?? 0;
  }

  /**
   * Aplica pontos diretamente (sem regra específica)
   */
  async applyDirectPoints(
    memberId: string,
    _userId: string,
    _points: number,
    _description: string
  ): Promise<number> {
    logger.warn('⚠️ [PointsService] applyDirectPoints() desativada (v1). Use fluxos v2 baseados em triggers.');
    const current = await this.getMemberPoints(memberId);
    return current?.totalPoints ?? 0;
  }

  // ==================== STREAKS ====================

  /**
   * Atualiza streak de um membro
   */
  async updateMemberStreak(
    _memberId: string,
    _streakType: 'treatment' | 'illness' | 'allopathic'
  ): Promise<void> {
    logger.warn('⚠️ [PointsService] updateMemberStreak() desativada (v1). Streaks são gerenciados por triggers v2.');
    return;
  }

  /**
   * Quebra streak de um membro
   */
  async breakMemberStreak(
    _memberId: string,
    _streakType: 'treatment' | 'illness' | 'allopathic'
  ): Promise<void> {
    logger.warn('⚠️ [PointsService] breakMemberStreak() desativada (v1). Streaks são gerenciados por triggers v2.');
    return;
  }

  // ==================== HISTÓRICO DE PONTOS ====================

  

  /**
   * Busca histórico de pontos de um membro com paginação
   * ✅ MIGRADO: Agora usa unified_point_history (legado + v2)
   */
  async getMemberPointHistory(
    memberId: string, 
    limit: number = 50,
    offset: number = 0
  ): Promise<PointEvent[]> {
    logger.info(`📊 [PointsService] Buscando histórico: membro=${memberId}, limit=${limit}, offset=${offset}`);
    
    // ✅ FASE 3: Usar view unificada (combina point_events + member_points_history)
    const { data, error } = await supabase
      .from('unified_point_history' as any)
      .select('id, user_id, member_id, description, points_change, badge_name, level_title, medication_detail, medical_detail, rule_id, changed_at, source')
      .eq('member_id', memberId)
      .order('changed_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      logger.error('❌ [PointsService] Erro ao buscar histórico do membro:', error);
      throw error;
    }

    logger.info(`✅ [PointsService] ${data.length} eventos encontrados`);

    // Adaptar estrutura da view para formato PointEvent
    return (data || []).map((item: any) => {
      const isLevelEvent = item.description === 'Nível atualizado';
      const levelTitle: string | undefined = item.level_title || undefined;
      const medicationDetail: string | undefined = item.medication_detail || undefined;
      const medicalDetail: string | undefined = item.medical_detail || undefined;
      const description = isLevelEvent
        ? levelTitle ?? item.description
        : item.badge_name || medicationDetail || medicalDetail || item.description || undefined;

      return {
        id: item.id,
        userId: item.user_id || undefined,
        memberId: item.member_id,
        familyId: undefined,
        eventType: item.description,
        points: item.points_change,
        description,
        badgeName: item.badge_name || undefined,
        levelTitle: isLevelEvent ? levelTitle : undefined,
        medicationDetail,
        medicalDetail,
        appliesTo: 'member',
        ruleId: item.rule_id,
        createdAt: item.changed_at,
        source: item.source // 'legacy' ou 'v2'
      } satisfies PointEvent;
    });
  }
  /**
   * Conta total de eventos de pontos de um membro
   */
  async getMemberPointHistoryCount(memberId: string): Promise<number> {
    const { count, error } = await supabase
      .from('unified_point_history' as any)
      .select('id', { count: 'exact', head: true })
      .eq('member_id', memberId);

    if (error) {
      logger.error('❌ [PointsService] Erro ao contar histórico:', error);
      return 0;
    }

    return count || 0;
  }

  /**
   * Busca histórico de pontos de uma família
   */
  async getFamilyPointHistory(familyId: string, limit: number = 50): Promise<PointEvent[]> {
    const { data, error } = await supabase
      .from('family_points_history' as any)
      .select('id, user_id, member_id, family_id, change_type, delta_points, description, created_at')
      .eq('family_id', familyId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      logger.error('Erro ao buscar histórico da família:', error);
      throw error;
    }

    return (data || []).map((item: any) => ({
      id: item.id,
      userId: item.user_id || undefined,
      memberId: item.member_id || undefined,
      familyId: item.family_id,
      eventType: item.change_type,
      points: item.delta_points ?? 0,
      description: item.description || undefined,
      badgeName: undefined,
      levelTitle: undefined,
      medicationDetail: undefined,
      medicalDetail: undefined,
      appliesTo: 'family',
      ruleId: undefined,
      createdAt: item.created_at,
      source: 'v2'
    } satisfies PointEvent));
  }

  // ==================== RANKING ====================

  /**
   * Busca ranking de membros de uma família
   * ✅ MIGRADO: Agora lê de member_points_v2
   */
  async getFamilyMembersRanking(familyId: string): Promise<MemberRanking[]> {
    const { data, error } = await supabase
      .from('member_points_v2' as any)
      .select(`
        member_id,
        total_points,
        level,
        family_members!inner(name, family_id)
      `)
      .eq('family_members.family_id', familyId)
      .order('total_points', { ascending: false });

    if (error) {
      logger.error('Erro ao buscar ranking da família:', error);
      throw error;
    }

    return (data || []).map((item: any, index: number) => ({
      memberId: item.member_id,
      name: item.family_members.name,
      totalPoints: item.total_points,
      level: item.level,
      rank: index + 1
    }));
  }

  /**
   * Busca ranking global de famílias
   */
  async getGlobalFamiliesRanking(limit: number = 10): Promise<FamilyRanking[]> {
    const { data, error } = await supabase
      .from('family_points' as any)
      .select(`
        family_id,
        total_points,
        level,
        families!inner(name)
      `)
      .order('total_points', { ascending: false })
      .limit(limit);

    if (error) {
      logger.error('Erro ao buscar ranking global:', error);
      throw error;
    }

    return (data || []).map((item: any, index: number) => ({
      familyId: item.family_id,
      familyName: item.families.name,
      totalPoints: item.total_points,
      level: item.level,
      rank: index + 1
    }));
  }

  // ==================== HELPERS ====================

  /**
   * Formata data no padrão YYYY-MM-DD
   */
  // Removidos helpers não utilizados (formatadores de data)

  /**
   * Formata timestamp no padrão ISO 8601 completo
   */
  // Removidos helpers não utilizados (formatadores de data/hora)

  // ==================== NÍVEIS ====================

  /**
   * Calcula nível baseado em pontos totais
   */
  calculateLevel(totalPoints: number): number {
    if (totalPoints >= 9001) return 10; // Mestre Homeopata
    if (totalPoints >= 6001) return 9;
    if (totalPoints >= 4001) return 8;
    if (totalPoints >= 2501) return 7;
    if (totalPoints >= 1501) return 6;
    if (totalPoints >= 1001) return 5;
    if (totalPoints >= 601) return 4;
    if (totalPoints >= 301) return 3;
    if (totalPoints >= 101) return 2;
    return 1;
  }

  /**
   * Retorna informações sobre o próximo nível
   */
  getNextLevelInfo(currentPoints: number): { nextLevel: number; pointsNeeded: number; progress: number } {
    const levelThresholds = [0, 101, 301, 601, 1001, 1501, 2501, 4001, 6001, 9001];
    const currentLevel = this.calculateLevel(currentPoints);
    
    if (currentLevel >= 10) {
      return {
        nextLevel: 10,
        pointsNeeded: 0,
        progress: 100
      };
    }

    const nextThreshold = levelThresholds[currentLevel];
    const prevThreshold = levelThresholds[currentLevel - 1];
    const pointsNeeded = nextThreshold - currentPoints;
    const progress = ((currentPoints - prevThreshold) / (nextThreshold - prevThreshold)) * 100;

    return {
      nextLevel: currentLevel + 1,
      pointsNeeded,
      progress: Math.round(progress)
    };
  }

  /**
   * Busca título de nível baseado em pontos e tipo (member ou family)
   */
  async getLevelTitle(points: number, appliesTo: 'member' | 'family'): Promise<LevelTitle | null> {
    const normalizedPoints = Math.max(points ?? 0, 0);

    const { data, error } = await supabase
      .from('level_titles' as any)
      .select('id, level, title, description, applies_to, icon, color, min_points, max_points, active, created_at, updated_at')
      .eq('applies_to', appliesTo)
      .eq('active', true)
      .lte('min_points', normalizedPoints)
      .order('min_points', { ascending: false })
      .limit(1)
      .single();

    if (error || !data) {
      if (error) {
        logger.error('Erro ao buscar título de nível:', error);
      }

      // Fallback explícito para nível 1 quando não houver título para a pontuação normalizada
      const fallbackTitle = await this.getLevelTitleByLevel(1, appliesTo);
      if (!fallbackTitle) {
        logger.warn('Título de nível padrão (nível 1) não encontrado.');
      }
      return fallbackTitle;
    }

    return convertLevelTitleFromDB(data);
  }

  /**
   * Busca todos os títulos de níveis
   */
  async getAllLevelTitles(appliesTo?: 'member' | 'family'): Promise<LevelTitle[]> {
    let query = supabase
      .from('level_titles' as any)
      .select('id, level, title, description, applies_to, icon, color, min_points, max_points, active, created_at, updated_at')
      .eq('active', true)
      .order('level', { ascending: true });

    if (appliesTo) {
      query = query.eq('applies_to', appliesTo);
    }

    const { data, error } = await query;

    if (error) {
      logger.error('Erro ao buscar títulos de níveis:', error);
      throw error;
    }

    return (data || []).map(convertLevelTitleFromDB);
  }

  /**
   * Busca título de nível específico
   */
  async getLevelTitleByLevel(level: number, appliesTo: 'member' | 'family'): Promise<LevelTitle | null> {
    const { data, error } = await supabase
      .from('level_titles' as any)
      .select('id, level, title, description, applies_to, icon, color, min_points, max_points, active, created_at, updated_at')
      .eq('level', level)
      .eq('applies_to', appliesTo)
      .eq('active', true)
      .single();

    if (error || !data) {
      logger.error('Erro ao buscar título de nível:', error);
      return null;
    }

    return convertLevelTitleFromDB(data);
  }

  // ==================== ESTATÍSTICAS ====================

  /**
   * Busca estatísticas globais do sistema
   */
  async getGlobalStats(): Promise<{
    totalPointsDistributed: number;
    totalFamilies: number;
    totalMembers: number;
    averagePointsPerMember: number;
    totalRules: number;
  }> {
    // Total de pontos distribuídos (membros + famílias)
    const { data: memberPointsData } = await supabase
      .from('member_points' as any)
      .select('total_points');

    const { data: familyPointsData } = await supabase
      .from('family_points' as any)
      .select('total_points');

    const totalMemberPoints = (memberPointsData || []).reduce((sum: number, item: any) => sum + item.total_points, 0);
    const totalFamilyPoints = (familyPointsData || []).reduce((sum: number, item: any) => sum + item.total_points, 0);

    // Contadores
    const { count: totalFamilies } = await supabase
      .from('family_points' as any)
      .select('id', { count: 'exact', head: true });

    const { count: totalMembers } = await supabase
      .from('member_points' as any)
      .select('id', { count: 'exact', head: true });

    const { count: totalRules } = await supabase
      .from('point_rules' as any)
      .select('id', { count: 'exact', head: true });

    const averagePointsPerMember = totalMembers && totalMembers > 0
      ? Math.round(totalMemberPoints / totalMembers)
      : 0;

    return {
      totalPointsDistributed: totalMemberPoints + totalFamilyPoints,
      totalFamilies: totalFamilies || 0,
      totalMembers: totalMembers || 0,
      averagePointsPerMember,
      totalRules: totalRules || 0
    };
  }

  /**
   * Verifica e atualiza streaks diários de um membro
   * Deve ser chamado ao abrir o app ou via job diário
   */
  async checkDailyStreaks(memberId: string): Promise<void> {
    logger.warn('⚠️ [PointsService] checkDailyStreaks() desativada (v1). Streaks são atualizados por jobs/triggers v2. memberId=', memberId);
    return;
  }

  // v1: funções privadas de verificação de streak removidas; jobs/triggers v2 fazem as atualizações diárias

  // v1: checkMissedDoses() removida; job daily_mark_missed_doses_v2 cobre o fluxo

  /**
   * Verifica streaks de todos os membros da família
   */
  async checkFamilyDailyStreaks(familyId: string): Promise<void> {
    logger.warn('⚠️ [PointsService] checkFamilyDailyStreaks() desativada (v1). Streaks são atualizados por jobs/triggers v2. familyId=', familyId);
    return;
  }

  // ==================== SISTEMA DE CONQUISTAS ====================

  /**
   * Detecta conquistas ao aplicar pontos
   * Retorna conquista se houver mudança de nível ou marco importante
   */
  async detectAchievements(
    _memberId: string,
    oldPoints: number,
    newPoints: number,
    _eventType: string
  ): Promise<Achievement | null> {
    const oldLevel = this.calculateLevel(oldPoints);
    const newLevel = this.calculateLevel(newPoints);

    // Conquista: Subiu de nível (ÚNICA CONQUISTA DE PONTOS)
    if (newLevel > oldLevel) {
      const levelTitle = await this.getLevelTitle(newPoints, 'member');
      
      return {
        type: 'level_up',
        title: `Nível ${newLevel} Alcançado!`,
        description: levelTitle?.title || `Você atingiu o nível ${newLevel}!`,
        points: 0, // Não dá pontos extras, apenas celebra
        icon: 'ri-trophy-line',
        metadata: {
          oldLevel,
          newLevel,
          levelTitle: levelTitle?.title,
          subtitle: `Você alcançou ${newPoints} pontos totais!` // Linha adicional
        }
      };
    }

    // Nota: Marcos de pontos (100, 500, 1000, 5000, 10000) foram REMOVIDOS
    // para evitar conflitos com mudanças de nível. Apenas conquistas de nível são exibidas.

    return null;
  }

  async markBadgeNotificationAsShown(memberBadgeId: string): Promise<boolean> {
    try {
      const { error } = await runOnlineOrToast(async () =>
        supabase
          .from('member_badges_v2')
          .update({ shown_at: new Date().toISOString() })
          .eq('id', memberBadgeId)
          .is('shown_at', null)
      );

      if (error) {
        logger.error('❌ [Badges] Erro ao marcar badge como exibido:', error);
        return false;
      }

      return true;
    } catch (error) {
      logger.error('❌ [Badges] Erro inesperado ao marcar badge como exibido:', error);
      return false;
    }
  }

  /**
   * Detecta conquistas de quiz
   */
  async detectQuizAchievement(
    _memberId: string,
    totalQuizzes: number
  ): Promise<Achievement | null> {
    const milestones = [5, 10, 25, 50, 100];
    
    for (const milestone of milestones) {
      if (totalQuizzes === milestone) {
        return {
          type: 'quiz_master',
          title: `Quiz Master ${milestone}!`,
          description: `Você completou ${milestone} quizzes!`,
          points: milestone * 10, // Bônus proporcional
          icon: 'ri-question-answer-line',
          metadata: {}
        };
      }
    }

    return null;
  }

  // ==================== BADGES SYSTEM ====================

  /**
   * Busca todos os badges disponíveis
   */
  async getAllBadges(category?: string): Promise<Badge[]> {
    let query = supabase
      .from('badges')
      .select('id, name, description, icon, category, tier, criteria, points_reward, color, active, created_at, updated_at')
      .eq('active', true)
      .order('tier', { ascending: true });

    if (category) {
      query = query.eq('category', category);
    }

    const { data, error } = await query;

    if (error) {
      logger.error('Erro ao buscar badges:', error);
      return [];
    }

    return (data || []).map(badge => ({
      id: badge.id,
      name: badge.name,
      description: badge.description,
      icon: badge.icon,
      category: badge.category,
      tier: badge.tier,
      criteria: badge.criteria,
      pointsReward: badge.points_reward,
      color: badge.color,
      active: badge.active,
      createdAt: badge.created_at,
      updatedAt: badge.updated_at
    }));
  }

  /**
   * Busca badges conquistados por um membro
   */
  async getMemberBadges(memberId: string): Promise<MemberBadge[]> {
    const { data, error } = await supabase
      .from('member_badges_v2')
      .select('id, member_id, badge_id, points_earned, unlocked_at, progress, metadata, created_at, shown_at, badge:badges(id, name, description, icon, category, tier, criteria, points_reward, color, active, created_at, updated_at)')
      .eq('member_id', memberId)
      .order('unlocked_at', { ascending: false });

    if (error) {
      logger.error('Erro ao buscar badges do membro:', error);
      return [];
    }

    return (data || []).map(mb => ({
      id: mb.id,
      memberId: mb.member_id,
      badgeId: mb.badge_id,
      pointsEarned: mb.points_earned ?? 0,
      unlockedAt: mb.unlocked_at,
      progress: mb.progress,
      metadata: mb.metadata,
      createdAt: mb.created_at,
      shownAt: mb.shown_at ?? null,
      badge: mb.badge ? {
        id: mb.badge.id,
        name: mb.badge.name,
        description: mb.badge.description,
        icon: mb.badge.icon,
        category: mb.badge.category,
        tier: mb.badge.tier,
        criteria: mb.badge.criteria,
        pointsReward: mb.badge.points_reward,
        color: mb.badge.color,
        active: mb.badge.active,
        createdAt: mb.badge.created_at,
        updatedAt: mb.badge.updated_at
      } : undefined
    }));
  }

  /**
   * Desbloqueia um badge para um membro
   */
  async unlockBadge(memberId: string, badgeId: string, metadata?: Record<string, any>): Promise<MemberBadge | null> {
    // Verificar se já possui o badge (usando API REST para evitar erro 406)
    try {
      const existingBadges = await pointsRestRequest(
        `member_badges_v2?member_id=eq.${memberId}&badge_id=eq.${badgeId}&select=id`,
        'GET'
      );

      if (existingBadges && existingBadges.length > 0) {
        logger.info('⚠️ [Badges] Membro já possui este badge');
        return null;
      }
    } catch (error) {
      logger.error('❌ [Badges] Erro ao verificar badge existente:', error);
      // Continua para tentar inserir - se já existir, o banco vai rejeitar
    }

    // Buscar informações do badge
    const { data: badge } = await supabase
      .from('badges')
      .select('id, name, description, icon, category, tier, criteria, points_reward, color, active, created_at, updated_at')
      .eq('id', badgeId)
      .single();

    if (!badge) {
      logger.error('❌ [Badges] Badge não encontrado');
      return null;
    }

    // Inserir badge conquistado na tabela v2 (trigger cuida da pontuação)
    const { data, error } = await supabase
      .from('member_badges_v2')
      .insert({
        member_id: memberId,
        badge_id: badgeId,
        points_earned: badge.points_reward ?? 0,
        unlocked_at: new Date().toISOString(),
        progress: 100,
        metadata: metadata ?? {}
      })
      .select('id, member_id, badge_id, points_earned, unlocked_at, progress, metadata, created_at, shown_at')
      .single();

    if (error) {
      if ((error as any).code === '23505') {
        logger.info('ℹ️ [Badges] Tentativa de duplicar badge ignorada (UNIQUE constraint)');
        return null;
      }
      logger.error('❌ [Badges] Erro ao desbloquear badge:', error);
      return null;
    }

    logger.info(`🏅 [Badges] Badge "${badge.name}" desbloqueado para membro ${memberId}`);
    logger.info('⚙️ [Badges] Trigger update_badges_points_v2() aplicará pontos automaticamente.');

    return {
      id: data.id,
      memberId: data.member_id,
      badgeId: data.badge_id,
      pointsEarned: data.points_earned ?? badge.points_reward ?? 0,
      unlockedAt: data.unlocked_at,
      progress: data.progress,
      metadata: data.metadata,
      createdAt: data.created_at,
      shownAt: data.shown_at ?? null,
      badge: {
        id: badge.id,
        name: badge.name,
        description: badge.description,
        icon: badge.icon,
        category: badge.category,
        tier: badge.tier,
        criteria: badge.criteria,
        pointsReward: badge.points_reward,
        color: badge.color,
        active: badge.active,
        createdAt: badge.created_at || new Date().toISOString(),
        updatedAt: badge.updated_at || new Date().toISOString()
      }
    };
  }

  /**
   * Retorna quantos dias o membro está ativo (desde a criação)
   */
  async getMemberActiveDays(memberId: string): Promise<number> {
    try {
      const { data: member } = await supabase
        .from('family_members')
        .select('created_at')
        .eq('id', memberId)
        .single();

      if (!member || !member.created_at) return 0;

      const createdDate = new Date(member.created_at);
      const today = new Date();
      const diffTime = Math.abs(today.getTime() - createdDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return diffDays;
    } catch (error) {
      logger.error('Erro ao calcular dias ativos:', error);
      return 0;
    }
  }

  /**
   * Retorna quantos quizzes o membro completou
   */
  async getMemberQuizCount(memberId: string): Promise<number> {
    const MIN_QUESTIONS_PER_QUIZ = 3;

    const countFromLocalStorage = () => {
      try {
        const quizHistoryKey = `quiz_history_${memberId}`;
        const historyStr = localStorage.getItem(quizHistoryKey);

        logger.info(`🧠 [PointsService] Fallback localStorage para ${memberId}`);
        logger.info(`📦 [PointsService] localStorage key: ${quizHistoryKey}`);
        logger.info(`📊 [PointsService] Histórico: ${historyStr}`);

        if (!historyStr) {
          logger.info('⚠️ [PointsService] Nenhum histórico local de quiz encontrado');
          return 0;
        }

        const history = JSON.parse(historyStr);
        if (!Array.isArray(history)) return 0;

        const completedEntries = history.filter(entry => {
          const answered = typeof entry?.questionsAnswered === 'number' ? entry.questionsAnswered : 0;
          return answered >= MIN_QUESTIONS_PER_QUIZ;
        });

        logger.info(`✅ [PointsService] Quizzes completos (fallback): ${completedEntries.length}`);
        return completedEntries.length;
      } catch (fallbackError) {
        logger.error('❌ [PointsService] Erro ao analisar fallback localStorage:', fallbackError);
        return 0;
      }
    };

    try {
      logger.info(`🧠 [PointsService] Contando quizzes completos (Supabase) para ${memberId}`);
      const { count, error } = await supabase
        .from('quiz_attempts' as any)
        .select('id', { count: 'exact', head: true })
        .eq('member_id', memberId)
        .eq('completed', true)
        .gte('questions_answered', MIN_QUESTIONS_PER_QUIZ);

      if (error) {
        logger.error('❌ [PointsService] Erro ao contar quizzes no Supabase:', error);
        return countFromLocalStorage();
      }

      const total = count ?? 0;
      logger.info(`✅ [PointsService] Quizzes completos (Supabase): ${total}`);
      return total;
    } catch (error) {
      logger.error('❌ [PointsService] Falha inesperada ao contar quizzes:', error);
      return countFromLocalStorage();
    }
  }

  /**
   * Retorna quantas doses o membro tomou no horário
   */
  async getMemberDosesOnTime(memberId: string): Promise<number> {
    try {
      logger.info(`💊 [PointsService] Contando doses no horário para ${memberId}`);

      const { data, error } = await supabase
        .from('member_points_history' as any)
        .select('id, change_reason')
        .eq('member_id', memberId)
        .in('change_reason', ['medication_dose', 'medication_dose_on_time']);

      if (error) {
        logger.error('Erro ao contar doses no horário (v2):', error);
        return 0;
      }

      const total = data?.length ?? 0;
      logger.info(`✅ [PointsService] Doses no horário encontradas: ${total}`);
      return total;
    } catch (error) {
      logger.error('Erro inesperado ao contar doses no horário:', error);
      return 0;
    }
  }
  /**
   * Retorna quantos tratamentos ativos o membro possui
   */
  async getMemberTreatmentCount(memberId: string): Promise<number> {
    try {
      logger.info(`🩺 [PointsService] Contando tratamentos ativos para ${memberId}`);

      const { count, error } = await supabase
        .from('medications_v2' as any)
        .select('id', { count: 'exact', head: true })
        .eq('member_id', memberId)
        .eq('is_active', true);

      if (error) {
        logger.error('Erro ao contar tratamentos (v2):', error);
        return 0;
      }

      const total = count ?? 0;
      logger.info(`✅ [PointsService] Tratamentos ativos encontrados: ${total}`);
      return total;
    } catch (error) {
      logger.error('Erro inesperado ao contar tratamentos (v2):', error);
      return 0;
    }
  }

  /**
   * Retorna quantos tratamentos concluídos o membro possui
   * Critério: medications_v2 com is_active=false e end_date <= hoje (DATE)
   */
  async getMemberCompletedTreatmentCount(memberId: string): Promise<number> {
    try {
      logger.info(`✅ [PointsService] Contando tratamentos concluídos para ${memberId}`);

      const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

      const { count, error } = await supabase
        .from('medications_v2' as any)
        .select('id', { count: 'exact', head: true })
        .eq('member_id', memberId)
        .eq('is_active', false)
        .not('end_date', 'is', null)
        .lte('end_date', today);

      if (error) {
        logger.error('Erro ao contar tratamentos concluídos (v2):', error);
        return 0;
      }

      const total = count ?? 0;
      logger.info(`✅ [PointsService] Tratamentos concluídos encontrados: ${total}`);
      return total;
    } catch (error) {
      logger.error('Erro inesperado ao contar tratamentos concluídos (v2):', error);
      return 0;
    }
  }

  /**
   * Verifica e desbloqueia badges automaticamente baseado em critérios
   * @param memberId - ID do membro
   * @param retroactive - Se true, marca badges como retroativos (sem notificação)
   */
  async checkAndUnlockBadges(memberId: string, retroactive: boolean = false): Promise<MemberBadge[]> {
    const unlockedBadges: MemberBadge[] = [];

    // Buscar pontos e streaks do membro
    const memberPoints = await this.getMemberPoints(memberId);
    if (!memberPoints) return [];

    // Buscar todos os badges disponíveis
    const allBadges = await this.getAllBadges();

    // Badges já conquistados
    const memberBadges = await this.getMemberBadges(memberId);
    const unlockedBadgeIds = memberBadges.map(mb => mb.badgeId);

    // 🚀 OTIMIZAÇÃO: Pre-fetch de todos os dados necessários EM PARALELO
    // Isso evita múltiplas queries sequenciais dentro do loop
    logger.info('⚡ [PointsService] Pre-fetching dados para badges...');
    const [daysActive, quizCount, dosesOnTime, treatmentCount, completedCount] = await Promise.all([
      this.getMemberActiveDays(memberId),
      this.getMemberQuizCount(memberId),
      this.getMemberDosesOnTime(memberId),
      this.getMemberTreatmentCount(memberId),
      this.getMemberCompletedTreatmentCount(memberId)
    ]);
    logger.info(`✅ [PointsService] Dados carregados: dias=${daysActive}, quiz=${quizCount}, doses=${dosesOnTime}, tratamentos=${treatmentCount}, concluidos=${completedCount}`);

    for (const badge of allBadges) {
      // Pular se já possui
      if (unlockedBadgeIds.includes(badge.id)) continue;

      let shouldUnlock = false;
      const metadata: Record<string, any> = {};

      // Verificar critérios (AGORA SEM AWAIT - usa variáveis pré-carregadas)
      switch (badge.criteria.type) {
        case 'treatment_streak':
          if (memberPoints.currentTreatmentStreak >= (badge.criteria.days || 0)) {
            shouldUnlock = true;
            metadata.streakDays = memberPoints.currentTreatmentStreak;
          }
          break;

        case 'illness_streak':
          if (memberPoints.currentIllnessStreak >= (badge.criteria.days || 0)) {
            shouldUnlock = true;
            metadata.streakDays = memberPoints.currentIllnessStreak;
          }
          break;

        case 'allopathic_streak':
          if (memberPoints.currentAllopathicStreak >= (badge.criteria.days || 0)) {
            shouldUnlock = true;
            metadata.streakDays = memberPoints.currentAllopathicStreak;
          }
          break;

        case 'level_reached':
          if (memberPoints.level >= (badge.criteria.level || 0)) {
            shouldUnlock = true;
            metadata.level = memberPoints.level;
          }
          break;

        case 'first_dose':
          // Badge "Primeiras Gotas" - desbloqueia na primeira pontuação de medicação
          if ((memberPoints.medicationPoints ?? 0) > 0) {
            shouldUnlock = true;
            metadata.firstDoseDate = new Date().toISOString();
            metadata.medicationPoints = memberPoints.medicationPoints;
          }
          break;

        case 'days_active':
          // Badge "Primeira Semana" - verifica dias desde a criação
          if (daysActive >= (badge.criteria.count || 0)) {
            shouldUnlock = true;
            metadata.daysActive = daysActive;
          }
          break;

        case 'quiz_completed':
          // Badge de Quiz - verifica total de quizzes completados
          if (quizCount >= (badge.criteria.count || 0)) {
            shouldUnlock = true;
            metadata.quizCount = quizCount;
          }
          break;

        case 'doses_on_time':
          // Badge "Homeopata Dedicado" - verifica doses no horário
          if (dosesOnTime >= (badge.criteria.count || 0)) {
            shouldUnlock = true;
            metadata.dosesOnTime = dosesOnTime;
          }
          break;

        case 'first_treatment':
          // Badge "Primeira Homeopatia" - verifica se criou primeiro tratamento
          if (treatmentCount >= (badge.criteria.count || 0)) {
            shouldUnlock = true;
            metadata.treatmentCount = treatmentCount;
          }
          break;

        case 'treatment_completed':
          // Badge "Tratamento Concluído" - verifica total de tratamentos concluídos
          if (completedCount >= (badge.criteria.count || 0)) {
            shouldUnlock = true;
            metadata.completedCount = completedCount;
          }
          break;
      }

      if (shouldUnlock) {
        // Adicionar flag retroactive ao metadata
        if (retroactive) {
          metadata.retroactive = true;
        }
        
        const unlockedBadge = await this.unlockBadge(memberId, badge.id, metadata);
        if (unlockedBadge) {
          unlockedBadges.push(unlockedBadge);
        }
      }
    }

    logger.info(`✅ [PointsService] Verificação concluída: ${unlockedBadges.length} badges desbloqueados`);
    return unlockedBadges;
  }

  /**
   * Calcula progresso para um badge específico
   */
  async getBadgeProgress(memberId: string, badgeId: string): Promise<number> {
    const memberPoints = await this.getMemberPoints(memberId);
    if (!memberPoints) return 0;

    const { data: badge } = await supabase
      .from('badges')
      .select('criteria')
      .eq('id', badgeId)
      .single();

    if (!badge) return 0;

    let progress = 0;

    switch (badge.criteria.type) {
      case 'treatment_streak':
        progress = Math.min(100, (memberPoints.currentTreatmentStreak / (badge.criteria.days || 1)) * 100);
        break;

      case 'illness_streak':
        progress = Math.min(100, (memberPoints.currentIllnessStreak / (badge.criteria.days || 1)) * 100);
        break;

      case 'allopathic_streak':
        progress = Math.min(100, (memberPoints.currentAllopathicStreak / (badge.criteria.days || 1)) * 100);
        break;

      case 'level_reached':
        progress = Math.min(100, (memberPoints.level / (badge.criteria.level || 1)) * 100);
        break;
    }

    return Math.round(progress);
  }
}

export default PointsService;

